let audioContext: AudioContext | null = null
let unlockInstalled = false
let lastChimeAt = 0

// Minimum interval between chimes so a burst of events plays a single alert.
const CHIME_THROTTLE_MS = 3000

const CHIME_TONE_FREQUENCIES_HZ = [880, 1174.66]

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined" || !("AudioContext" in window)) return null
  if (!audioContext) audioContext = new AudioContext()
  return audioContext
}

/**
 * Browsers block audio until the user interacts with the page. Resuming the
 * AudioContext on the first gesture unlocks playback for alerts that arrive
 * later without any interaction.
 */
export function installNotificationAudioUnlock(): void {
  if (unlockInstalled || typeof window === "undefined") return
  unlockInstalled = true

  const unlock = () => {
    const context = getAudioContext()
    if (context && context.state === "suspended") void context.resume()
    window.removeEventListener("pointerdown", unlock)
    window.removeEventListener("keydown", unlock)
  }

  window.addEventListener("pointerdown", unlock)
  window.addEventListener("keydown", unlock)
}

/**
 * Plays a short two-tone chime synthesized with the Web Audio API, so no
 * audio asset needs to be shipped. Throttled to avoid rapid-fire alerts.
 */
export function playNotificationChime(): void {
  const now = Date.now()
  if (now - lastChimeAt < CHIME_THROTTLE_MS) return

  const context = getAudioContext()
  if (!context) return
  if (context.state === "suspended") {
    // Not unlocked yet; resume for future plays but skip this one.
    void context.resume()
    return
  }

  lastChimeAt = now
  const chimeStart = context.currentTime

  CHIME_TONE_FREQUENCIES_HZ.forEach((frequency, toneIndex) => {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.type = "sine"
    oscillator.frequency.value = frequency

    const toneStart = chimeStart + toneIndex * 0.12
    gain.gain.setValueAtTime(0, toneStart)
    gain.gain.linearRampToValueAtTime(0.3, toneStart + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.001, toneStart + 0.45)

    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.start(toneStart)
    oscillator.stop(toneStart + 0.5)
  })
}
